'use client';

import { useEffect, useEffectEvent, useRef } from 'react';

import {
  faListCheck,
  faPause,
  faPlay,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from 'cn';

import { Button } from '@/shared-ui';

import type { HandsFreeQuestionPanelValue } from './project-usability-handsfree-content.types';
import { HandsFreePopupSelectionProvider } from './project-usability-handsfree-popup-context';
import ProjectUsabilityHandsFreeQuestionPopup from './project-usability-handsfree-question-popup';
import { isWithinTaskSheetDismissGrace } from './project-usability-mobile-controls.helpers';

interface Props extends HandsFreeQuestionPanelValue {
  open: boolean;
  awaitingTaskCompletion: boolean;
}

export default function ProjectUsabilityMobileTaskSheet({
  open,
  currentQuestionIndex,
  totalQuestions,
  lastAssistantMessage,
  replyFeedbackMessage,
  projectType,
  onSubmitContent,
  isSubmitting,
  awaitingTaskCompletion,
  effectiveIsConversationOver,
  onConversationOver,
  conversationOverText,
  onReportProblemSubmit,
  popupSelectionValue,
  onPopupSelectionChange,
  onPlayPauseClick,
  showPlayIcon,
  playPauseAriaLabel,
  isSessionOver,
  onClose,
  onMouseEnter,
  onMouseLeave,
}: Props) {
  const openedAtRef = useRef(0);

  const handleEscape = useEffectEvent(() => onClose());

  useEffect(() => {
    if (!open) return;
    openedAtRef.current = Date.now();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleEscape();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const handleScrimClick = () => {
    if (isWithinTaskSheetDismissGrace(openedAtRef.current, Date.now())) return;
    onClose();
  };

  if (!open || !lastAssistantMessage) return null;

  const questionNumber = currentQuestionIndex + 1;

  return (
    <dialog
      open
      className='fixed inset-0 z-50 m-0 flex size-full max-h-none max-w-none flex-col justify-end border-0 bg-transparent p-0'
      data-test='mobile-task-sheet'
      aria-modal='true'
      aria-label={`Task ${questionNumber} of ${totalQuestions}`}
    >
      {/* reuse-exception: empty full-bleed dismiss overlay; Button requires children and injects control styles */}
      <button
        type='button'
        className='absolute inset-0 bg-black/40 motion-safe:animate-fade-in'
        aria-label='Close task panel'
        data-test='mobile-task-sheet-scrim'
        onClick={handleScrimClick}
      />

      <div
        className={cn(
          'relative flex max-h-[85vh] flex-col items-center overflow-y-auto rounded-t-4xl bg-white p-4',
          'pb-[calc(env(safe-area-inset-bottom)+1rem)] drop-shadow-[0_4px_24px_#34346F3D]',
          'motion-safe:animate-slide-in-from-bottom'
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <span
          className='h-1 w-16 shrink-0 rounded-full bg-gray-300'
          aria-hidden='true'
        />

        <div className='flex w-full flex-col gap-4 px-2 pt-4'>
          <div className='flex items-start gap-1'>
            <div className='flex min-w-0 flex-1 flex-col gap-1'>
              <div className='flex items-center gap-1'>
                <FontAwesomeIcon
                  icon={faListCheck}
                  fixedWidth
                  className='shrink-0 text-gray-500'
                  aria-hidden='true'
                />
                <span className='text-sm font-semibold text-gray-900'>
                  Tasks
                </span>
              </div>
              <span className='text-xs text-gray-500'>
                {questionNumber} of {totalQuestions} completed
              </span>
            </div>

            <Button
              variant='ghost'
              size='icon'
              type='button'
              onClick={onPlayPauseClick}
              disabled={isSessionOver}
              className='size-10 shrink-0 rounded-full bg-gray-100 text-gray-900 hover:enabled:bg-gray-200'
              aria-label={playPauseAriaLabel}
              data-test='mobile-task-sheet-pause-button'
            >
              <FontAwesomeIcon
                icon={showPlayIcon ? faPlay : faPause}
                fixedWidth
                className='text-xl'
              />
            </Button>

            <Button
              variant='ghost'
              size='icon'
              type='button'
              onClick={onClose}
              className='size-10 shrink-0 rounded-full bg-gray-100 text-gray-900 hover:enabled:bg-gray-200'
              aria-label='Close task panel'
              data-test='mobile-task-sheet-close-button'
            >
              <FontAwesomeIcon icon={faXmark} fixedWidth className='text-xl' />
            </Button>
          </div>

          <HandsFreePopupSelectionProvider
            value={popupSelectionValue}
            onChange={onPopupSelectionChange}
          >
            <ProjectUsabilityHandsFreeQuestionPopup
              variant='sheet'
              currentQuestionIndex={currentQuestionIndex}
              totalQuestions={totalQuestions}
              message={lastAssistantMessage}
              projectType={projectType}
            >
              <ProjectUsabilityHandsFreeQuestionPopup.Body
                replyFeedbackMessage={replyFeedbackMessage}
                isConversationOver={effectiveIsConversationOver}
                isSubmitting={isSubmitting}
                awaitingTaskCompletion={awaitingTaskCompletion}
                onSubmitContent={onSubmitContent}
                onReportProblemSubmit={onReportProblemSubmit}
              />
              {effectiveIsConversationOver && onConversationOver && (
                <ProjectUsabilityHandsFreeQuestionPopup.Footer
                  onConversationOver={onConversationOver}
                  conversationOverText={conversationOverText}
                />
              )}
            </ProjectUsabilityHandsFreeQuestionPopup>
          </HandsFreePopupSelectionProvider>
        </div>
      </div>
    </dialog>
  );
}
